import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api/client.js';
import { useRefreshSession } from '../auth/useSession.js';
import { Alert, Button, Card, Field, Input } from '../components/ui.js';

/**
 * The password step.
 *
 * A success here does not mean the user is in — the server issues a
 * half-authenticated session and the shell moves them on to the second factor.
 * This screen deliberately does not decide what comes next; it refreshes the
 * session and lets the server's answer drive the routing.
 */
export function LoginPage() {
  const refreshSession = useRefreshSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await api.post('/api/auth/login', { email, password });
      await refreshSession();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Something went wrong. Please try again.',
      );
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-sunken px-4 py-12">
      <div className="w-full max-w-sm">
        <header className="mb-6 text-center">
          <h1 className="text-lg font-semibold tracking-tight text-strong">Amazon Seller App</h1>
          <p className="mt-1 text-sm text-muted">FBA inventory and sell-through</p>
        </header>

        <Card className="p-6">
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            {error && <Alert>{error}</Alert>}

            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                autoFocus
                disabled={busy}
              />
            </Field>

            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={busy}
              />
            </Field>

            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          {/*
            A plain link, not a client route: the sign-in screen is the signed-out
            shell, and /forgot is a public page matched before the session is
            consulted. Accounts are still created by an administrator, who invites
            them; this is the self-service path back in for a forgotten password.
          */}
          <p className="mt-4 text-center text-sm">
            <a href="/forgot" className="text-brand-700 underline underline-offset-2">
              Forgot your password?
            </a>
          </p>
        </Card>

        <p className="mt-4 text-center text-xs text-muted">
          Need access? Ask an administrator to invite you.
        </p>
        <p className="mt-2 text-center text-xs text-faint">
          Two-factor authentication is required on every account.
        </p>
      </div>
    </div>
  );
}
